import type { DecodedBrowserAppointment } from '@/features/appointments/types';

export interface AppointmentRowSection {
  month: string;
  items: DecodedBrowserAppointment[];
}

const monthLabel = (appointment: DecodedBrowserAppointment): string =>
  new Date(appointment.currentSchedule.startAt).toLocaleDateString('en-US', {
    month: 'long',
    timeZone: appointment.currentSchedule.timezone,
    year: 'numeric',
  });

// Groups pre-filtered, pre-sorted native appointments by their per-row
// month label using CONTIGUOUS RUNS — a same-name label that follows a
// different-name label opens a new section rather than merging back into
// the earlier same-name section.
//
// The chronological ordering matters more than section deduplication:
// merging an A/B/A label sequence (which happens when appointments carry
// different timezones and the per-appointment-tz month straddles a UTC
// boundary) into two sections A[a1, a2], B[b1] would silently reorder
// the display to a1 → a2 → b1, dropping the b1 item that sat between
// the two a's in the caller-supplied absolute-instant order. Contiguous
// runs keep the visible sequence identical to the sorted input.
//
// The caller is responsible for filtering and sorting —
// `useNativeAppointmentRows` owns those rules. This helper is pure and
// stateless.
export const groupAppointmentsByMonth = (
  appointments: readonly DecodedBrowserAppointment[],
): AppointmentRowSection[] => {
  const sections: AppointmentRowSection[] = [];
  appointments.forEach((appointment) => {
    const month = monthLabel(appointment);
    const lastSection = sections.at(-1);
    if (lastSection?.month === month) {
      lastSection.items.push(appointment);
    } else {
      sections.push({ month, items: [appointment] });
    }
  });
  return sections;
};
